import { useState, useEffect } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Textarea,
} from '@heroui/react';
import type { Marca, MarcaRequest } from '@/types/marca.model';

interface MarcaEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (id: string | number, data: MarcaRequest['data']) => Promise<void>;
  loading?: boolean;
  marca?: Marca;
}

const MarcaEditModal = ({ isOpen, onClose, onSubmit, loading, marca }: MarcaEditModalProps) => {
  const [formData, setFormData] = useState<Omit<MarcaRequest['data'], 'tenantId' | 'locale' | 'localizations'>>({
    nombre: '',
    descripcion: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load marca data when the modal opens or marca updates
  useEffect(() => {
    if (marca) {
      setFormData({
        nombre: marca.nombre || '',
        descripcion: marca.descripcion || '',
      });
    } else {
      setFormData({
        nombre: '',
        descripcion: '',
      });
    }
    setErrors({});
  }, [marca, isOpen]);

  const handleChange = (name: string, value: string) => {
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  const handleSubmit = async () => {
    // Basic validation
    const newErrors: Record<string, string> = {}
    if (!formData.nombre.trim()) newErrors.nombre = 'Nombre es requerido';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      if (marca?.id) {
        await onSubmit(marca.id, formData);
        // Clear any existing errors
        setErrors({});
        // Close the modal after successful submission
        onClose();
      }
    } catch (error) {
      // Optionally handle submission errors here
      console.error('Error submitting form:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    handleChange(e.target.name, e.target.value);
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      placement="top-center"
      backdrop="blur"
      size="2xl"
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">Editar Marca</ModalHeader>
            <ModalBody className="space-y-4">
                <hr/>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleInputChange}
                  variant="bordered"
                  isRequired
                  errorMessage={errors.nombre}
                  className="md:col-span-2"
                />
                <Textarea
                  label="Descripción"
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleInputChange}
                  variant="bordered"
                  errorMessage={errors.descripcion}
                  className="md:col-span-2"
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button 
                color="danger" 
                variant="light" 
                onPress={onClose}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button 
                color="primary" 
                onPress={handleSubmit}
                isLoading={loading}
              >
                Actualizar Marca
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default MarcaEditModal;